import axios from 'axios'
import { kpRequest, kpParams, kpEndpoint } from '@/axios'

// KP-3919: browser-direct multipart upload engine for brand-catalogue files.
// The file is sliced into parts and PUT straight to S3 via presigned URLs —
// our backend only signs and finalises, so there is no size limit, no proxy
// timeout exposure, and a network blip costs one part, not the file.
//
// Progress is reported live from the browser's own upload events; the
// backend's file_uploads row (updated per part) is the durable state that
// pause/refresh/resume and the status poll rely on.

const PARALLEL_PARTS = 4
const PART_PUT_ATTEMPTS = 3
const RETRY_DELAY_MS = 800

export class UploadCancelled extends Error {
  constructor() {
    super('Upload cancelled')
    this.name = 'UploadCancelled'
  }
}

// Internal: an in-flight part PUT was aborted by cancel. The worker treats it
// as an interruption rather than a failure, so it never consumes a retry.
const PART_INTERRUPTED = Symbol('part-interrupted')
const partInterrupted = () => Object.assign(new Error('Part interrupted'), { [PART_INTERRUPTED]: true })
const isPartInterrupted = err => Boolean(err && err[PART_INTERRUPTED])

// Shared control handle.
//
// PAUSE does not touch the network: the parts already in flight are allowed
// to finish and bank their ETags, and the workers simply stop picking up new
// ones. Aborting them would throw away up to 4x25MB of completed transfer
// and re-send it on resume. The UI still reacts instantly because the
// progress display freezes on the flag (see `report` below), so "paused"
// is immediate to the user while the bytes already paid for still land.
//
// CANCEL does abort in flight — there is nothing to preserve.
export function createUploadControl() {
  const interrupters = new Set()
  return {
    paused: false,
    cancelled: false,
    onInterrupt(fn) {
      interrupters.add(fn)
      return () => interrupters.delete(fn)
    },
    interrupt() {
      interrupters.forEach(fn => fn())
      interrupters.clear()
    },
    pause() {
      this.paused = true
    },
    resume() {
      this.paused = false
    },
    cancel() {
      this.cancelled = true
      this.interrupt()
    },
  }
}

const sleep = ms => new Promise(resolve => { setTimeout(resolve, ms) })

// Holding means: paused, and not since cancelled (cancel wins over pause).
const isHolding = control => Boolean(control && control.paused && !control.cancelled)

const waitWhilePaused = async control => {
  while (isHolding(control)) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(250)
  }
}

const assertActive = control => {
  if (control && control.cancelled) throw new UploadCancelled()
}

// Raw S3 part PUT with plain axios: no app auth headers (the signature IS
// the auth), with per-part retry and live byte progress. Cancel aborts the
// request mid-transfer through the control's interrupt hook; pause does not
// — an in-flight part is worth more finished than abandoned.
//
// `signUrl` is called per attempt rather than once per part: a presigned URL
// has a fixed lifetime, so retrying a slow or stalled part against the same
// URL can keep replaying an expired signature until the attempts run out and
// the whole upload fails. Re-signing costs one cheap call, and only on the
// paths that already failed.
const putPart = async (signUrl, blob, onBytes, control) => {
  let lastError
  for (let attempt = 1; attempt <= PART_PUT_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const url = await signUrl()
    const source = axios.CancelToken.source()
    const unregister = control && control.onInterrupt
      ? control.onInterrupt(() => source.cancel('interrupted'))
      : () => {}
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await axios.put(url, blob, {
        cancelToken: source.token,
        onUploadProgress: e => onBytes(e.loaded),
        // Presigned PUT: any extra header would break the signature.
        transformRequest: [data => data],
      })
      unregister()
      return res.headers.etag
    } catch (err) {
      unregister()
      onBytes(0)
      if (axios.isCancel(err)) throw partInterrupted()
      lastError = err
      // eslint-disable-next-line no-await-in-loop
      await sleep(RETRY_DELAY_MS * attempt)
    }
  }
  throw lastError
}

/**
 * Post-refresh resume: ask the backend whether an in-flight upload of this
 * exact file (owner + type + name + size) already exists. Returns the
 * `resumeFrom` shape for uploadMultipart, or null to start fresh.
 */
export async function findResumableUpload(file, fileType) {
  const res = await kpRequest({
    ...kpEndpoint.multipartUploads.findPending,
    // lastModified narrows the match: name + size alone can collide (two
    // exports of the same report), and resuming the wrong file would splice
    // unrelated bytes together with nothing to show for it.
    payload: {
      fileType,
      fileName: file.name,
      size: file.size,
      ...(file.lastModified ? { lastModified: file.lastModified } : {}),
    },
  })
  return res.data.data.upload || null
}

/**
 * Give up on a half-done upload row (marks it cancelled and aborts the S3
 * multipart) so the next attempt of the same file starts clean instead of
 * re-matching a poisoned row.
 */
export const abandonUpload = uploadRecordId => kpRequest({
  ...kpParams(kpEndpoint.multipartUploads.abort, uploadRecordId),
})

const signPartUrl = async (uploadRecordId, partNumber) => {
  const res = await kpRequest({
    ...kpParams(kpEndpoint.multipartUploads.partUrls, uploadRecordId),
    payload: { partNumbers: [partNumber] },
  })
  return res.data.data.urls[0].url
}

const recordPart = (uploadRecordId, partNumber, etag) => kpRequest({
  ...kpParams(kpEndpoint.multipartUploads.partDone, uploadRecordId),
  payload: { partNumber, etag },
})

/**
 * Upload one file. Resolves to the {url, name} the collection stores.
 * `onProgress` fires with {loaded, total, percent}; `control` (from
 * createUploadControl) supports pause/resume/cancel. `resumeFrom` carries a
 * previous attempt's {uploadRecordId, doneParts} after a page refresh.
 */
export async function uploadMultipart(file, {
  fileType, onProgress, control, resumeFrom,
}) {
  let uploadRecordId
  let partSize
  let partsTotal
  const donePartNumbers = new Set()

  if (resumeFrom) {
    uploadRecordId = resumeFrom.uploadRecordId
    partSize = resumeFrom.partSize
    partsTotal = resumeFrom.partsTotal
    resumeFrom.doneParts.forEach(n => donePartNumbers.add(n))
  } else {
    const res = await kpRequest({
      ...kpEndpoint.multipartUploads.initiate,
      payload: {
        fileType,
        fileName: file.name,
        size: file.size,
        ...(file.lastModified ? { lastModified: file.lastModified } : {}),
      },
    });
({ uploadRecordId, partSize, partsTotal } = res.data.data)
  }

  const partBytes = new Array(partsTotal).fill(0)
  donePartNumbers.forEach(n => {
    partBytes[n - 1] = Math.min(partSize, file.size - (n - 1) * partSize)
  })
  // Monotonic display: a retried part restarts its byte count, so hold the
  // peak rather than letting the bar walk backwards.
  //
  // `loadedTotal` is kept as a running sum. Re-reducing every part on each
  // progress event is O(parts) per tick across all workers, which on the very
  // large files this engine exists for is exactly where the main thread can
  // least afford it.
  let loadedTotal = partBytes.reduce((a, b) => a + b, 0)
  let peakLoaded = loadedTotal
  const setPartBytes = (index, value) => {
    loadedTotal += value - partBytes[index]
    partBytes[index] = value
  }
  const report = () => {
    if (!onProgress) return
    // Paused freezes what the user sees. Parts still completing in the
    // background keep advancing the real total, so the first report after
    // resume steps forward — never back.
    if (control && control.paused) return
    peakLoaded = Math.max(peakLoaded, loadedTotal)
    const loaded = peakLoaded
    onProgress({
      loaded,
      total: file.size,
      percent: Math.min(100, Math.round((loaded / file.size) * 100)),
      uploadRecordId,
    })
  }
  report()

  let nextPart = 1
  const takePart = () => {
    while (nextPart <= partsTotal && donePartNumbers.has(nextPart)) nextPart += 1
    if (nextPart > partsTotal) return null
    const part = nextPart
    nextPart += 1
    return part
  }

  // The first hard failure stops every worker. Promise.all rejects on it, but
  // the siblings are independent loops: without this they keep PUTting parts
  // and recording them after the caller has already been told the upload
  // failed, mutating server-side state behind a UI that says it stopped.
  let failure = null
  const noteFailure = err => {
    if (failure) return
    failure = err
    // Abort the siblings' in-flight PUTs too — the upload is over.
    if (control && control.interrupt) control.interrupt()
  }

  // One part, start to finish: slice, sign, PUT, record, mark done.
  const uploadOnePart = async partNumber => {
    const start = (partNumber - 1) * partSize
    const blob = file.slice(start, Math.min(start + partSize, file.size))
    const etag = await putPart(
      () => signPartUrl(uploadRecordId, partNumber),
      blob,
      loaded => {
        setPartBytes(partNumber - 1, Math.min(loaded, blob.size))
        report()
      },
      control,
    )
    await recordPart(uploadRecordId, partNumber, etag)
    setPartBytes(partNumber - 1, blob.size)
    donePartNumbers.add(partNumber)
    report()
  }

  const worker = async () => {
    // An interrupted part is retried by the same worker — re-signed, since
    // its URL may have expired during a long pause.
    let interrupted = null
    while (!failure) {
      assertActive(control)
      // eslint-disable-next-line no-await-in-loop
      await waitWhilePaused(control)
      assertActive(control)
      const partNumber = interrupted !== null ? interrupted : takePart()
      interrupted = null
      if (partNumber === null) return
      try {
        // eslint-disable-next-line no-await-in-loop
        await uploadOnePart(partNumber)
      } catch (err) {
        if (!isPartInterrupted(err)) {
          noteFailure(err)
          throw err
        }
        // Re-queued for this worker; the loop re-checks `failure` first, so a
        // failure elsewhere ends the worker rather than re-uploading.
        interrupted = partNumber
      }
    }
  }

  try {
    await Promise.all(
      Array.from({ length: Math.min(PARALLEL_PARTS, partsTotal) }, worker),
    )
  } catch (err) {
    if (err instanceof UploadCancelled) {
      await kpRequest({
        ...kpParams(kpEndpoint.multipartUploads.abort, uploadRecordId),
      }).catch(() => {})
    }
    throw err
  }

  const done = await kpRequest({
    ...kpParams(kpEndpoint.multipartUploads.complete, uploadRecordId),
  })
  return done.data.data
}
